import { Component, OnInit, OnDestroy, HostListener, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { Auth } from '@angular/fire/auth';
import {
  Firestore,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  onSnapshot,
  serverTimestamp,
} from '@angular/fire/firestore';
import { loadStripe, Stripe, StripeCardElement } from '@stripe/stripe-js';
import { environment } from 'src/app/environments/environment';

import {
  SubscriptionPlanService,
  AdminPlan,
} from '../../subscription-management/subscription-services/subscription-plan.service';

export interface Plan {
  id: string;
  name: string;
  fee: number;
  lf: number | null;
  ml: number | null;
  mb: number;
  ao: boolean;
  mkt: boolean;
  col: string;
  tag?: string;
  icon: string;
  tagline: string;
  quarterlyDiscount: number;
  halfYearlyDiscount: number;
  yearlyDiscount: number;
}

export interface BillingTerm {
  id: string;
  label: string;
  months: number;
  discount: number;
}

export interface FeatureRow {
  label: string;
  tooltip?: string;
  cells: { [planId: string]: boolean | string };
}

export interface FeatureSection {
  title: string;
  icon: string;
  rows: FeatureRow[];
}

export interface FaqItem {
  question: string;
  answer: string;
  open: boolean;
}

export interface PlatformOffer {
  enabled: boolean;
  badgeText: string;
  highlightText: string;
  bodyText: string;
  countdownLabel: string;
  expiryDate: string;
}

@Component({
  selector: 'app-vehicle-plan',
  templateUrl: './vehicle-plan-dialog.component.html',
  styleUrls: ['./vehicle-plan-dialog.component.scss']
})
export class VehiclePlanDialogComponent implements OnInit, OnDestroy {

  private destroy$ = new Subject<void>();
  private _onboardingUnsub: (() => void) | null = null;

  selectedTerm!: BillingTerm;
  selectedPlan!: Plan;
  showStickyBar = false;

  plans: Plan[] = [];
  terms: BillingTerm[] = [];
  featureSections: FeatureSection[] = [];

  // ── Platform Offer ───────────────────────────────────────
  platformOffer: PlatformOffer | null = null;

  /** Human-readable countdown label derived from expiryDate if set */
  get offerExpiryLabel(): string {
    if (!this.platformOffer?.expiryDate) return '';
    const expiry = new Date(this.platformOffer.expiryDate);
    const now = new Date();
    if (expiry <= now) return 'Offer expired';
    const diffMs = expiry.getTime() - now.getTime();
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays === 1) return 'Ends tomorrow';
    if (diffDays <= 7) return `Ends in ${diffDays} days`;
    return `Ends ${expiry.toLocaleDateString('en-AE', { day: 'numeric', month: 'short' })}`;
  }

  // ── Onboarding guard ─────────────────────────────────────
  /** True when the user has no plan yet — hides the Back to Dashboard button */
  isMandatoryOnboarding = false;

  // ── Payment Modal ────────────────────────────────────────
  showPaymentModal = false;
  isFreePlanActivating = false;
  isPaymentProcessing = false;
  paymentError = '';
  showFreeTrialWelcomeModal = false;
  showFreePlanSuccessModal = false;
  activatedFreePlanName = '';
  private animationFrameId: number | null = null;

  private _planPaymentSubmitted = false;
  get isPlanPaymentLocked(): boolean {
    return this._planPaymentSubmitted;
  }

  /** true  = plan activated immediately (free → paid)
   *  false = upgrade scheduled for end of current cycle (paid → higher paid) */
  paymentSuccess = false;
  isImmediateActivation = true;
  upgradeDate = '';   // ISO string from backend, formatted for display

  /**
   * True while openPaymentModal() is calling createCheckoutIntent.
   * The Pay button is hidden (not just disabled) during this window so the
   * user can never submit payment before a valid client secret exists.
   */
  modalInitializing = false;

  // ── Stripe ───────────────────────────────────────────────
  private stripe: Stripe | null = null;
  private cardElement: StripeCardElement | null = null;
  private stripeInitialized = false;

  /**
   * Client secret created ONCE per modal session in openPaymentModal().
   * pay() reuses this secret — it never calls createCheckoutIntent again,
   * which prevents a new PaymentIntent (and charge) on every retry.
   */
  private _clientSecret = '';

  // ── FAQs ─────────────────────────────────────────────────
  faqs: FaqItem[] = [
    {
      question: 'How does the 3-month free trial work?',
      answer: 'Upgrade to Starter or Growth today and enjoy full marketplace access at no cost for your first 3 months. You only pay after the promotional period ends — no hidden fees, no upfront charge.',
      open: false
    },
    {
      question: 'Can I switch plans at any time?',
      answer: 'Yes. Upgrades take effect at the end of your current 30-day billing cycle. Downgrades apply at the start of your next cycle.',
      open: false
    },
    {
      question: 'What happens to my listings if I cancel?',
      answer: 'Your account reverts to the Free tier at the end of your paid period. Listings exceeding the Free limit (2 slots) are deactivated but not deleted — reactivate them anytime by upgrading again.',
      open: false
    },
    {
      question: 'Are vehicle listing fees included in the subscription?',
      answer: 'No. Vehicle listing fees are charged separately when you activate a listing from your dashboard. The subscription fee covers platform access, bookings, analytics, and other plan features.',
      open: false
    },
    {
      question: 'How do add-on booking packs work?',
      answer: 'On any paid plan you can purchase extra manual booking capacity at the add-on price at any time during your billing cycle. Add-on credits do not roll over to the next period.',
      open: false
    },
    {
      question: 'What does annual billing save me?',
      answer: 'Annual billing saves you on the platform subscription fee compared to paying monthly. The full amount is charged upfront. Discounts apply to the subscription only, not to vehicle listing fees.',
      open: false
    }
  ];

  constructor(
    private router: Router,
    private planService: SubscriptionPlanService,
    private functions: Functions,
    private auth: Auth,
    // ── ADDED: Firestore for free-plan activation ──────────
    private firestore: Firestore,
    private ngZone: NgZone,
  ) { }

  // ── Lifecycle ────────────────────────────────────────────

  ngOnInit(): void {
    this.terms = this.planService.terms;
    this.selectedTerm = this.terms[0];

    this.planService.getPlans()
      .pipe(takeUntil(this.destroy$))
      .subscribe((adminPlans: AdminPlan[]) => {

        const activePlans = adminPlans.filter(p => p.isActive && p.monthlyFee > 0);

        this.plans = activePlans.map(p => this.toVendorPlan(p));

        this.featureSections = this.planService.buildFeatureSections(activePlans) as FeatureSection[];

        if (this.selectedPlan) {
          const refreshed = this.plans.find(p => p.id === this.selectedPlan.id);
          this.selectedPlan = refreshed ?? this.plans[0];
        } else {
          this.selectedPlan =
            this.plans.find(p => p.name.toLowerCase() === 'starter') ?? this.plans[0];
        }
      });

    // Load platform-wide offer from Firestore
    this.loadPlatformOffer();

    // Check whether this is a mandatory onboarding visit (no plan set yet)
    this.checkMandatoryOnboarding();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.cardElement) {
      this.cardElement.destroy();
    }
    if (this._onboardingUnsub) {
      this._onboardingUnsub();
    }
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }
  }

  // ── Platform Offer ───────────────────────────────────────

  private async loadPlatformOffer(): Promise<void> {
    try {
      const offer = await this.planService.getPlatformOffer();
      // Only show if enabled and not expired
      if (offer?.enabled) {
        if (offer.expiryDate) {
          const expiry = new Date(offer.expiryDate);
          if (expiry <= new Date()) {
            this.platformOffer = null;
            return;
          }
        }
        this.platformOffer = offer as PlatformOffer;
      } else {
        this.platformOffer = null;
      }
    } catch (err) {
      console.error('Could not load platform offer:', err);
      this.platformOffer = null;
    }
  }

  // ── AdminPlan → vendor Plan ──────────────────────────────

  private toVendorPlan(p: AdminPlan): Plan {
    return {
      id: p.id,
      name: p.name,
      fee: p.monthlyFee,
      lf: p.marketplaceEnabled ? p.listingFee : null,
      ml: p.marketplaceEnabled
        ? (p.activeListingLimit === 0 ? 0 : p.activeListingLimit)
        : null,
      mb: p.bookingLimit === 0 ? 999 : p.bookingLimit,
      ao: p.addOnBookingEnabled,
      mkt: p.marketplaceEnabled,
      col: p.color,
      tag: p.tag || undefined,
      icon: p.icon ?? 'fa-star',
      tagline: p.tagline,
      quarterlyDiscount: p.quarterlyDiscount ?? 5,
      halfYearlyDiscount: p.halfYearlyDiscount ?? 10,
      yearlyDiscount: p.yearlyDiscount ?? 15,
    };
  }

  // ── Price Calculation ────────────────────────────────────

  getDiscount(plan: Plan, term: BillingTerm): number {
    switch (term.id) {
      case 'quarterly': return plan.quarterlyDiscount;
      case 'halfYearly': return plan.halfYearlyDiscount;
      case 'yearly': return plan.yearlyDiscount;
      default: return 0;
    }
  }

  monthlyRate(plan: Plan, term: BillingTerm): number {
    if (plan.fee === 0) return 0;
    return Math.round(plan.fee * (1 - this.getDiscount(plan, term) / 100));
  }

  calcPrice(plan: Plan, term: BillingTerm): number {
    if (plan.fee === 0) return 0;
    return this.monthlyRate(plan, term) * term.months;
  }

  getSavings(plan: Plan, term: BillingTerm): number {
    return plan.fee * term.months - this.calcPrice(plan, term);
  }

  currentMonthlyRate(plan: Plan): number {
    return this.monthlyRate(plan, this.selectedTerm);
  }

  calcTotal(): number {
    return this.calcPrice(this.selectedPlan, this.selectedTerm);
  }

  hasDiscount(plan: Plan): boolean {
    return plan.fee > 0 && this.getDiscount(plan, this.selectedTerm) > 0;
  }

  currentDiscount(plan: Plan): number {
    return this.getDiscount(plan, this.selectedTerm);
  }

  // ── UI Helpers ───────────────────────────────────────────

  @HostListener('window:scroll')
  onScroll(): void {
    this.showStickyBar = window.scrollY > 500;
  }

  selectPlan(plan: Plan): void { this.selectedPlan = plan; }
  selectTerm(term: BillingTerm): void { this.selectedTerm = term; }

  isBoolean(val: boolean | string): boolean {
    return typeof val === 'boolean';
  }

  cellValue(row: FeatureRow, planId: string): boolean | string {
    return row.cells[planId] ?? false;
  }

  getPlanPerks(p: Plan): string[] {
    const perks: string[] = [];

    if (p.ml === null) {
      perks.push('Marketplace not included');
    } else {
      perks.push(p.ml === 0
        ? 'Unlimited marketplace listings'
        : `${p.ml} active marketplace listings`
      );
    }

    if (p.lf !== null) {
      perks.push(p.lf > 0
        ? `${p.lf} AED per vehicle listing (30 days)`
        : 'No per-vehicle listing fee'
      );
    }

    perks.push(p.mb === 999
      ? 'Unlimited manual bookings per cycle'
      : `${p.mb} manual bookings per cycle`
    );

    if (p.ao) perks.push('Add-on booking packs available');
    if (!p.mkt) perks.push('Dashboard & analytics only');

    return perks;
  }

  toggleFaq(faq: FaqItem): void { faq.open = !faq.open; }

  goDashboard(): void {
    // During mandatory onboarding (no plan yet) the back button is hidden
    // in the template, but guard here too so deep-links cannot bypass it.
    if (this.isMandatoryOnboarding) return;
    this.router.navigate(['/business/Dashboard']);
  }

  // ── Payment Flow ─────────────────────────────────────────

  async openPaymentModal(): Promise<void> {
    if (!this.selectedPlan) return;

    // ── PAYMENT ENFORCEMENT CHECK: Verify vendor has paymentsEnabled !== false ──
    const currentUid = this.auth.currentUser?.uid;
    if (currentUid) {
      try {
        const userDoc = await getDoc(doc(this.firestore, 'users-list', currentUid));
        if (userDoc.exists()) {
          const uData = userDoc.data() as any;
          if (uData.paymentsEnabled === false) {
            this.paymentError = 'Payment actions have been disabled for this account by administration. Please contact support.';
            alert(this.paymentError);
            return;
          }
        }
      } catch (e) {
        console.error('Error checking vendor payment permissions:', e);
      }
    }

    // ── FREE PLAN: activate in Firestore, then navigate on success ──────
    if (this.selectedPlan.fee === 0) {
      this.isFreePlanActivating = true;
      this.paymentError = '';

      const success = await this.activateFreePlan();

      this.isFreePlanActivating = false;

      if (success) {
        this.activatedFreePlanName = this.selectedPlan.name;
        this.showFreePlanSuccessModal = true;
        this.triggerCelebration();
        setTimeout(() => {
          this.showFreePlanSuccessModal = false;
          this.router.navigate(['/business/Dashboard']);
        }, 3000);
      }
      // If activation failed, paymentError is already set and displayed
      // in the template; we do NOT navigate.
      return;
    }

    // ── PAID PLAN ────────────────────────────────────────────────────────
    // Open the modal immediately so the user gets instant feedback, then
    // create the PaymentIntent in the background.  The Pay button is hidden
    // (modalInitializing = true) until the client secret is ready, so the
    // user can never click Pay before a valid intent exists.
    this.showPaymentModal = true;
    this.paymentError = '';
    this.paymentSuccess = false;
    this.isPaymentProcessing = false;
    this.modalInitializing = true;
    this._clientSecret = '';
    this._planPaymentSubmitted = false;

    try {
      // ── Create PaymentIntent ONCE per modal session ──────────────────
      const createIntent = httpsCallable(this.functions, 'createCheckoutIntent');
      const result: any = await createIntent({
        planId: this.selectedPlan.id,
        termId: this.selectedTerm.id,
      });

      const { clientSecret, isImmediate, upgradeDate } = result.data;
      this._clientSecret = clientSecret;
      this.isImmediateActivation = isImmediate;
      this.upgradeDate = this.formatDate(upgradeDate);
      this.modalInitializing = false;

      // Mount the card element once the payment form replaces the loader
      setTimeout(() => this.initStripeCard(), 150);

    } catch (err: any) {
      this.paymentError = err?.message || 'Failed to prepare payment. Please close and try again.';
      this.modalInitializing = false;
    }
  }

  private async initStripeCard(): Promise<void> {
    if (this.stripeInitialized) return;

    this.stripe = await loadStripe(environment.stripePublishableKey);
    if (!this.stripe) return;

    const elements = this.stripe.elements();
    this.cardElement = elements.create('card', {
      style: {
        base: {
          fontFamily: "'Inter', system-ui, sans-serif",
          fontSize: '15px',
          color: '#0F172A',
          '::placeholder': { color: '#94A3B8' },
        },
        invalid: { color: '#EF4444' },
      }
    });

    this.cardElement.mount('#stripe-card-element');
    this.stripeInitialized = true;
  }

  closePaymentModal(): void {
    if (this.isPaymentProcessing) return;
    this.showPaymentModal = false;
    this.stripeInitialized = false;
    this.modalInitializing = false;
    this._clientSecret = '';   // force fresh intent on next open
    this._planPaymentSubmitted = false;
    if (this.cardElement) {
      this.cardElement.destroy();
      this.cardElement = null;
    }
  }

  async pay(): Promise<void> {
    // Double-submission guard: block if already processing OR if no client
    // secret is available (intent not yet created or already consumed).
    if (!this.stripe || !this.cardElement) return;
    if (this.isPaymentProcessing || this._planPaymentSubmitted) return;
    if (!this._clientSecret) {
      this.paymentError = 'Payment session is not ready. Please close and try again.';
      return;
    }

    this.isPaymentProcessing = true;
    this._planPaymentSubmitted = true;
    this.paymentError = '';

    try {
      // ── Confirm card payment using the existing PaymentIntent ────────────
      // We reuse this._clientSecret (created once in openPaymentModal) so
      // retries after a card error do NOT create a new charge.
      const { paymentIntent, error } = await this.stripe.confirmCardPayment(
        this._clientSecret,
        { payment_method: { card: this.cardElement } }
      );

      if (error) {
        this.paymentError = error.message || 'Payment failed.';
        this.isPaymentProcessing = false;
        return;
      }

      if (paymentIntent?.status === 'succeeded') {
        // ── Tell the backend to activate or schedule the plan ─────────────
        const applyFn = httpsCallable(this.functions, 'applySubscription');
        const applyResult: any = await applyFn({
          paymentIntentId: paymentIntent.id,
        });

        this.isPaymentProcessing = false;
        this.paymentSuccess = true;
        this.isImmediateActivation = applyResult.data.activated;
        if (applyResult.data.upgradeDate) {
          this.upgradeDate = this.formatDate(applyResult.data.upgradeDate);
        }

        setTimeout(() => {
          this.showPaymentModal = false;
          this.router.navigate(['/business/Dashboard']);
        }, 2500);

      } else {
        this.paymentError = 'Payment was not completed. Please try again.';
        this.isPaymentProcessing = false;
      }

    } catch (err: any) {
      this.paymentError = err?.message || 'Something went wrong.';
      this.isPaymentProcessing = false;
    }
  }

  // ── Mandatory Onboarding Check ───────────────────────────

  /**
   * Listens to the current user's Firestore document.
   * If no plan is set or subscription has expired, flips isMandatoryOnboarding = true
   * so the "Back to Dashboard" button is hidden from the template.
   */
  private checkMandatoryOnboarding(): void {
    const uid = this.auth.currentUser?.uid;
    if (!uid) return;

    const userDocRef = doc(this.firestore, 'users-list', uid);

    this._onboardingUnsub = onSnapshot(
      userDocRef,
      (snap) => {
        this.ngZone.run(() => {
          const data = snap.exists() ? snap.data() : null;
          const hasPlan = !!(
            data?.['plan'] ||
            data?.['planId'] ||
            data?.['accountdetails']?.['plan'] ||
            data?.['accountdetails']?.['planId']
          );

          // subscriptionStatus='active' is the definitive signal set by the backend
          const subStatus = data?.['subscriptionStatus'] ?? '';
          const isActive = subStatus === 'active';

          let isDateExpired = false;
          if (!isActive) {
            const rawExpiry = data?.['subscriptionExpiry'] || data?.['accountdetails']?.['subscriptionExpiry'];
            if (rawExpiry) {
              const expiryDate = typeof rawExpiry.toDate === 'function'
                ? rawExpiry.toDate()
                : new Date(rawExpiry);
              isDateExpired = expiryDate < new Date();
            }
          }

          // Mandatory only when: no plan at all, OR status not active AND date has lapsed
          this.isMandatoryOnboarding = !hasPlan || (!isActive && isDateExpired);

          // Only show Free Trial modal if they have NO plan at all (brand new vendor)
          if (!hasPlan) {
            this.showFreeTrialWelcomeModal = true;
          }
        });
      },
      (error) => {
        this.ngZone.run(() => {
          console.error('Error listening to user document:', error);
          // If the read fails we default to showing the button — safe fallback.
          this.isMandatoryOnboarding = false;
        });
      }
    );
  }

  // ── Free Plan Activation ─────────────────────────────────

  /**
   * Writes the selected free plan ID to the current user's Firestore document
   * (users-list/{uid}) so that the layout guard detects a plan on next load.
   * Only called when selectedPlan.fee === 0.
   * Returns true on success, false on failure.
   */
  private async activateFreePlan(): Promise<boolean> {
    const uid = this.auth.currentUser?.uid;
    if (!uid || !this.selectedPlan) return false;

    try {
      const userRef = doc(this.firestore, 'users-list', uid);
      await updateDoc(userRef, {
        plan: this.selectedPlan.name || this.selectedPlan.id,
        planId: this.selectedPlan.id,
        planName: this.selectedPlan.name,
        planActivatedAt: new Date().toISOString(),
        subscriptionStatus: 'active',
        // Free plan has no expiry; set a far-future sentinel so
        // downstream renewal-date logic never breaks.
        subscriptionExpiry: new Date(
          new Date().setFullYear(new Date().getFullYear() + 100)
        ).toISOString(),
        term: this.selectedTerm.id,
      });
      return true;
    } catch (err: any) {
      console.error('Free plan activation failed:', err);
      this.paymentError = err?.message || 'Could not activate free plan. Please try again.';
      return false;
    }
  }

  // ── Free Trial Activation ────────────────────────────────

  /**
   * Automatically initializes the 'free_trial' plan in Firestore if it doesn't exist.
   */
  private async ensureFreeTrialPlanExists(): Promise<void> {
    try {
      const planRef = doc(this.firestore, 'subscription_plans', 'free_trial');
      const snap = await getDoc(planRef);
      if (!snap.exists()) {
        await setDoc(planRef, {
          planId: 'free_trial',
          name: '3-Month Free Trial',
          description: 'Enjoy 3 months of unlimited marketplace access completely free.',
          color: '#42B9F5',
          tag: 'Trial',
          icon: 'fa-gift',
          monthlyPrice: 0,
          listingPrice: 0,
          maxListings: 0,
          bookingLimit: 0,
          extraBookingEnabled: true,
          extraBookingPrice: 0,
          extraBookingCount: 99999,
          marketplaceEnabled: true,
          listedEquipmentModuleEnabled: true,
          listingDays: 30,
          bookingCycleDays: 30,
          graceDays: 5,
          trialEnabled: true,
          trialDays: 90,
          trialVehicleCap: 99999,
          listingExpiryReminderEnabled: true,
          listingExpiryReminderDays: 3,
          storageLimitGb: 0,
          quarterlyDiscount: 0,
          halfYearlyDiscount: 0,
          yearlyDiscount: 0,
          archiveMonths: 999,
          public: false,
          active: true,
          displayOrder: 0,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }
    } catch (err) {
      console.error('Could not ensure free trial plan exists:', err);
    }
  }

  /**
   * Activates the 3-Month Free Trial for a new vendor.
   * Sets up 90 days subscription and infinite quotas.
   */
  async activateFreeTrial(): Promise<void> {
    const uid = this.auth.currentUser?.uid;
    if (!uid) return;

    this.isFreePlanActivating = true;
    this.paymentError = '';

    try {
      await this.ensureFreeTrialPlanExists();

      const userRef = doc(this.firestore, 'users-list', uid);
      const now = new Date();
      const expiry = new Date();
      expiry.setDate(now.getDate() + 90);

      await updateDoc(userRef, {
        plan: '3-Month Free Trial',
        planId: 'free_trial',
        planName: '3-Month Free Trial',
        subscriptionStatus: 'active',
        subscriptionStart: now.toISOString(),
        subscriptionStartDate: serverTimestamp(),
        subscriptionExpiry: expiry.toISOString(),
        subscriptionExpiryDate: expiry,
        subscriptionCycleEnd: new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000),
        subscriptionTermId: 'trial',
        manualBookingQuota: 99999,
        manualBookingsUsed: 0,
        cycleStart: now.toISOString(),
        term: 'trial',
        updatedAt: now.toISOString()
      });

      this.showFreeTrialWelcomeModal = false;
      this.isFreePlanActivating = false;
      this.router.navigate(['/business/Dashboard']);
    } catch (err: any) {
      console.error('Free trial activation failed:', err);
      this.paymentError = err?.message || 'Could not activate free trial. Please try again.';
      this.isFreePlanActivating = false;
    }
  }

  /** Format an ISO date string for display, e.g. "20 Jun 2026". */
  formatDate(iso: string): string {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString('en-AE', {
      day: 'numeric', month: 'short', year: 'numeric'
    });
  }

  triggerCelebration(): void {
    // Cancel any existing animation frame
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }

    setTimeout(() => {
      const canvas = document.getElementById('celebration-canvas') as HTMLCanvasElement;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const resizeCanvas = () => {
        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;
      };
      resizeCanvas();
      window.addEventListener('resize', resizeCanvas);

      interface Particle {
        x: number;
        y: number;
        vx: number;
        vy: number;
        size: number;
        color: string;
        alpha: number;
        decay: number;
        gravity: number;
        rotation?: number;
        rotationSpeed?: number;
        shape?: 'circle' | 'square' | 'spark';
      }

      const particles: Particle[] = [];
      const colors = ['#42B9F5', '#8B5CF6', '#10B981', '#F59E0B', '#EF4444', '#EC4899', '#3B82F6', '#14B8A6'];

      const createExplosion = (x: number, y: number, particleCount = 80) => {
        // Shockwave rings / cracker pop particles
        for (let i = 0; i < particleCount; i++) {
          const angle = Math.random() * Math.PI * 2;
          const speed = 4 + Math.random() * 14;
          const shape = Math.random() > 0.4 ? 'circle' : (Math.random() > 0.5 ? 'square' : 'spark');
          particles.push({
            x,
            y,
            vx: Math.cos(angle) * speed,
            vy: Math.sin(angle) * speed - (2 + Math.random() * 4),
            size: 5 + Math.random() * 8,
            color: colors[Math.floor(Math.random() * colors.length)],
            alpha: 1,
            decay: 0.008 + Math.random() * 0.012,
            gravity: 0.12 + Math.random() * 0.08,
            rotation: Math.random() * Math.PI * 2,
            rotationSpeed: (Math.random() - 0.5) * 0.2,
            shape: shape as any
          });
        }
      };

      const width = window.innerWidth;
      const height = window.innerHeight;

      // Launch cracker explosions
      createExplosion(width / 2, height / 2 - 100, 100);

      setTimeout(() => {
        createExplosion(width * 0.35, height / 2 - 150, 70);
      }, 200);

      setTimeout(() => {
        createExplosion(width * 0.65, height / 2 - 150, 70);
      }, 400);

      setTimeout(() => {
        for (let i = 0; i < 3; i++) {
          setTimeout(() => {
            createExplosion(
              width * 0.25 + Math.random() * (width * 0.5),
              height * 0.3 + Math.random() * (height * 0.3),
              50
            );
          }, i * 200);
        }
      }, 700);

      const render = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        for (let i = particles.length - 1; i >= 0; i--) {
          const p = particles[i];
          p.x += p.vx;
          p.y += p.vy;
          p.vy += p.gravity;
          p.vx *= 0.97; // air resistance
          p.vy *= 0.97;
          p.alpha -= p.decay;

          if (p.rotation !== undefined && p.rotationSpeed !== undefined) {
            p.rotation += p.rotationSpeed;
          }

          if (p.alpha <= 0) {
            particles.splice(i, 1);
            continue;
          }

          ctx.save();
          ctx.globalAlpha = p.alpha;
          ctx.translate(p.x, p.y);
          if (p.rotation !== undefined) {
            ctx.rotate(p.rotation);
          }
          ctx.fillStyle = p.color;

          if (p.shape === 'square') {
            ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size);
          } else if (p.shape === 'spark') {
            ctx.beginPath();
            ctx.moveTo(0, -p.size);
            ctx.lineTo(p.size / 3, 0);
            ctx.lineTo(0, p.size);
            ctx.lineTo(-p.size / 3, 0);
            ctx.closePath();
            ctx.fill();
          } else {
            ctx.beginPath();
            ctx.arc(0, 0, p.size / 2, 0, Math.PI * 2);
            ctx.fill();
          }
          ctx.restore();
        }

        if (particles.length > 0) {
          this.animationFrameId = requestAnimationFrame(render);
        } else {
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          window.removeEventListener('resize', resizeCanvas);
        }
      };

      render();
    }, 50);
  }
}